import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Group,
  Indicator,
  Menu,
  ScrollArea,
  Stack,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { NotificationList } from "./components";
import NotificationEmptyState from "./components/NotificationEmptyState/NotificationEmptyState";

import { IconBell, IconChevronDown } from "@/components/icons";
import {
  getNotificationCountByFilter,
  useNotificationCounts,
} from "@/services/notifications/get-notification-count";
import { useNotifications } from "@/services/notifications/get-notifications";
import { useUpdateNotificationStatus } from "@/services/notifications/update-notification-status";

import type { Notification } from "@/types/notification";
import type { FC } from "react";

export type NotificationMenuProps = {
  notifications?: Notification[];
};

export const NotificationMenu: FC<NotificationMenuProps> = ({ notifications = [] }) => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure();
  const [filterMenuOpened, setFilterMenuOpened] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState<"all" | "delivered" | "read">("all");

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const selectColor = isDark ? "#a1b0e0" : "#143AB1";

  const { mutate: updateStatus, isPending: isUpdating } = useUpdateNotificationStatus();

  const { data: response, isPending: isLoadingNotifications } = useNotifications();
  const { data: notificationCounts } = useNotificationCounts();

  const currentNotifications = useMemo<Notification[]>(() => {
    if (response?.results) return response.results;
    return Array.isArray(notifications) ? notifications : [];
  }, [notifications, response?.results]);

  const unreadNotifications = useMemo(
    () => currentNotifications.filter((notification) => notification.status === "delivered"),
    [currentNotifications]
  );

  const readNotifications = useMemo(
    () => currentNotifications.filter((notification) => notification.status === "read"),
    [currentNotifications]
  );

  const unread = (notificationCounts?.delivered ?? unreadNotifications.length) > 0;

  const filteredNotifications = useMemo(
    () =>
      notificationFilter === "all"
        ? currentNotifications
        : currentNotifications.filter((notification) => notification.status === notificationFilter),
    [currentNotifications, notificationFilter]
  );

  function markAllAsRead() {
    unreadNotifications.forEach((notification) => {
      updateStatus({ id: notification.id, status: "read" });
    });
  }

  const showEmptyState = !isLoadingNotifications && filteredNotifications.length === 0;

  const notificationCount = getNotificationCountByFilter(notificationCounts, notificationFilter, {
    total: response?.total ?? currentNotifications.length,
    delivered: unreadNotifications.length,
    read: readNotifications.length,
  });

  const canMarkAllAsRead = unreadNotifications.length > 0;

  const FILTER_LABELS: Record<"all" | "delivered" | "read", string> = {
    all: "All",
    delivered: "Unread",
    read: "Read",
  };

  function selectNotificationFilter(filter: "all" | "delivered" | "read") {
    setNotificationFilter(filter);
    setFilterMenuOpened(false);
  }

  return (
    <Menu
      opened={opened}
      onChange={(isOpen) => {
        if (isOpen) {
          open();
          return;
        }

        if (filterMenuOpened) return;

        setFilterMenuOpened(false);
        close();
      }}
      position="bottom-end"
      shadow="md"
      closeOnItemClick={false}
      styles={{
        dropdown: {
          overflow: "visible",
        },
      }}
    >
      <Menu.Target>
        <Box component="span" display="inline-block">
          <ActionIcon c="white" variant="subtle" aria-label={t("common.open_menu")}>
            <Indicator color="warning" display="flex" size={8} offset={8} disabled={!unread}>
              <IconBell />
            </Indicator>
          </ActionIcon>
        </Box>
      </Menu.Target>

      <Menu.Dropdown
        w={400}
        maw="100%"
        p={0}
        mah="90%"
        style={{
          overflow: "visible",
        }}
      >
        <Stack pt="lg" gap={0} style={{ overflow: "visible" }}>
          <Flex
            px="lg"
            justify="space-between"
            align="center"
            w="100%"
            gap="sm"
            wrap="wrap"
            style={{ overflow: "visible" }}
          >
            <Flex
              justify="flex-start"
              align="center"
              gap="md"
              wrap="wrap"
              style={{ flex: 1, minWidth: 0, overflow: "visible" }}
            >
              <Badge
                variant="light"
                style={{
                  borderRadius: "50%",
                  minWidth: 24,
                  height: 24,
                  padding: "0 6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {notificationCount}
              </Badge>

              <Title order={4}>{t("components.NotificationMenu.title")}</Title>

              <Menu
                opened={filterMenuOpened}
                onChange={setFilterMenuOpened}
                position="bottom-start"
                shadow="md"
                withinPortal={false}
              >
                <Menu.Target>
                  <Group
                    gap={4}
                    wrap="nowrap"
                    style={{ cursor: "pointer" }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Box component="span" c={selectColor} fz={14} fw={500}>
                      {FILTER_LABELS[notificationFilter]}
                    </Box>
                    <IconChevronDown size={14} color={selectColor} />
                  </Group>
                </Menu.Target>

                <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
                  <Menu.Item
                    onClick={(event) => {
                      event.stopPropagation();
                      selectNotificationFilter("all");
                    }}
                  >
                    {t("components.NotificationMenu.all")}
                  </Menu.Item>
                  <Menu.Item
                    onClick={(event) => {
                      event.stopPropagation();
                      selectNotificationFilter("delivered");
                    }}
                  >
                    {t("components.NotificationMenu.unread")}
                  </Menu.Item>
                  <Menu.Item
                    onClick={(event) => {
                      event.stopPropagation();
                      selectNotificationFilter("read");
                    }}
                  >
                    {t("components.NotificationMenu.read")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>

            <Group wrap="nowrap" style={{ flexShrink: 0 }}>
              {canMarkAllAsRead && notificationFilter !== "read" && (
                <Button
                  size="compact-xs"
                  variant="transparent"
                  onClick={markAllAsRead}
                  disabled={isUpdating}
                  aria-label={t("components.NotificationMenu.mark_all_as_read")}
                >
                  {t("components.NotificationMenu.mark_all_as_read")}
                </Button>
              )}
            </Group>
          </Flex>

          <Box style={{ overflow: "hidden" }}>
            {showEmptyState ? (
              <Center h={280} px="lg">
                <NotificationEmptyState />
              </Center>
            ) : (
              <ScrollArea h={450} mah="100%">
                <NotificationList
                  items={filteredNotifications}
                  onRead={(id) => {
                    const notification = currentNotifications.find((item) => item.id === id);

                    if (!notification) return;

                    updateStatus({
                      id,
                      status: notification.status === "delivered" ? "read" : "delivered",
                    });
                  }}
                />
              </ScrollArea>
            )}
          </Box>
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
};
